/**
 * SEO e GEO (otimização para buscadores e para assistentes de IA)
 *
 * Fonte única dos metadados das páginas públicas, dos dados estruturados
 * (JSON-LD schema.org), do sitemap e dos arquivos llms.txt / llms-full.txt.
 * Tudo é gerado a partir do mesmo conteúdo exibido no site (content.ts e
 * segments.ts), então as informações para robôs nunca ficam diferentes do
 * que o visitante lê.
 *
 * Usado no build pela pré-renderização (src/prerender/entry-server.tsx).
 *
 * @author Equipe VendeAi
 * @since 1.3.0
 */

import type { SubscriptionPlan } from '@services/platformService';
import {
  BRAND,
  FEATURE_GROUPS,
  PAIN_POINTS,
  FAQ_ITEMS,
  ONBOARDING_STEPS,
  COMPARISON_ROWS,
} from './content';
import { SEGMENT_PAGES, SegmentPage } from './segments';
import { SITE_URL } from './usePageMeta';
import { BLOG_ARTICLES, BLOG_TITLE, BLOG_DESCRIPTION, plainText } from './blog';
import type { BlogArticleWithMeta } from './blog/types';
import { PRIVACY_PATH, PRIVACY_POLICY_READY } from './legal/company';
import { PRIVACY_DESCRIPTION, PRIVACY_TITLE } from './legal/privacyPolicy';

/**
 * Perfis oficiais da empresa (Instagram, LinkedIn, YouTube...), separados por
 * vírgula em VITE_SOCIAL_PROFILES. Entram no JSON-LD (sameAs) para buscadores
 * e IAs reconhecerem a empresa. Os definidos no painel Super Admin têm
 * prioridade (lidos da API na pré-renderização). Ver docs/seo/03-perfis-oficiais.md.
 */
export const OFFICIAL_PROFILES = (import.meta.env.VITE_SOCIAL_PROFILES || '')
  .split(',')
  .map(p => p.trim())
  .filter(p => /^https:\/\//.test(p));

export const HOME_TITLE = `${BRAND} | Cardápio digital, robô de WhatsApp e gestão sem taxa por pedido`;
export const HOME_DESCRIPTION =
  'Cardápio digital com link próprio, robô que fecha pedidos no WhatsApp, QR Code na mesa, PIX confirmado no seu banco e gestão completa para pizzarias, hamburguerias, restaurantes, padarias e barbearias. Teste grátis por 30 dias.';

/** Descrição curta e factual da empresa, repetida em JSON-LD e llms.txt */
export const COMPANY_SUMMARY =
  'O Vende Aí é um sistema brasileiro (SaaS) de cardápio digital, pedidos e gestão para delivery e salão. O estabelecimento vende pelo próprio link, pelo robô de WhatsApp e pelo QR Code da mesa, recebe PIX e cartão direto na própria conta e paga uma mensalidade fixa, sem comissão por pedido.';

export interface PublicPage {
  path: string;
  title: string;
  description: string;
  kind: 'home' | 'segment' | 'blog' | 'article' | 'legal';
  segment?: SegmentPage;
  article?: BlogArticleWithMeta;
}

/** Páginas públicas pré-renderizadas e listadas no sitemap */
export const PUBLIC_PAGES: PublicPage[] = [
  { path: '/', title: HOME_TITLE, description: HOME_DESCRIPTION, kind: 'home' },
  ...SEGMENT_PAGES.map(segment => ({
    path: `/para/${segment.slug}`,
    title: segment.seoTitle,
    description: segment.seoDescription,
    kind: 'segment' as const,
    segment,
  })),
  { path: '/blog', title: BLOG_TITLE, description: BLOG_DESCRIPTION, kind: 'blog' },
  ...BLOG_ARTICLES.map(article => ({
    path: `/blog/${article.slug}`,
    title: `${article.title} | ${BRAND}`,
    description: article.description,
    kind: 'article' as const,
    article,
  })),
  // A política só entra no sitemap e na pré-renderização quando estiver
  // completa (ver legal/company.ts); o rascunho fica fora dos buscadores
  ...(PRIVACY_POLICY_READY
    ? [
        {
          path: PRIVACY_PATH,
          title: PRIVACY_TITLE,
          description: PRIVACY_DESCRIPTION,
          kind: 'legal' as const,
        },
      ]
    : []),
];

const url = (path: string) => `${SITE_URL}${path === '/' ? '/' : path}`;

const money = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function paidPlans(plans: SubscriptionPlan[]) {
  return plans.filter(p => !p.isTrial && p.priceMonthly > 0);
}

// =========================================================================
// JSON-LD (schema.org)
// =========================================================================

type JsonLd = Record<string, unknown>;

function organization(profiles: string[] = OFFICIAL_PROFILES): JsonLd {
  return {
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organizacao`,
    name: BRAND,
    url: url('/'),
    logo: `${SITE_URL}/logo-512.png`,
    description: COMPANY_SUMMARY,
    areaServed: { '@type': 'Country', name: 'Brasil' },
    knowsLanguage: 'pt-BR',
    ...(profiles.length > 0 ? { sameAs: profiles } : {}),
  };
}

function website(): JsonLd {
  return {
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#site`,
    name: BRAND,
    url: url('/'),
    inLanguage: 'pt-BR',
    publisher: { '@id': `${SITE_URL}/#organizacao` },
  };
}

function softwareApplication(plans: SubscriptionPlan[], segment?: SegmentPage): JsonLd {
  const paid = paidPlans(plans);
  const prices = paid.map(p => p.priceMonthly);
  const features = [
    ...FEATURE_GROUPS.flatMap(g => g.items.map(i => i.title)),
    ...(segment ? segment.solutions.map(s => s.title) : []),
  ];
  return {
    '@type': 'SoftwareApplication',
    '@id': `${SITE_URL}/#software`,
    name: BRAND,
    url: url(segment ? `/para/${segment.slug}` : '/'),
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: segment
      ? `Sistema para ${segment.audience}`
      : 'Sistema de cardápio digital, delivery e gestão para restaurantes',
    operatingSystem: 'Web, Android, iOS (aplicativo instalável PWA)',
    inLanguage: 'pt-BR',
    description: segment ? segment.seoDescription : COMPANY_SUMMARY,
    featureList: Array.from(new Set(features)),
    audience: {
      '@type': 'BusinessAudience',
      audienceType: segment
        ? segment.audience
        : 'Pizzarias, hamburguerias, lanchonetes, restaurantes, padarias, confeitarias, barbearias, açaiterias, marmitarias e comércios',
    },
    publisher: { '@id': `${SITE_URL}/#organizacao` },
    ...(prices.length > 0
      ? {
          offers: {
            '@type': 'AggregateOffer',
            priceCurrency: 'BRL',
            lowPrice: Math.min(...prices).toFixed(2),
            highPrice: Math.max(...prices).toFixed(2),
            offerCount: paid.length,
            description:
              'Mensalidade fixa, pedidos ilimitados, sem comissão por pedido. Teste grátis de 30 dias sem cartão de crédito.',
          },
        }
      : {}),
  };
}

function faqPage(path: string, items: { question: string; answer: string }[]): JsonLd {
  return {
    '@type': 'FAQPage',
    '@id': `${url(path)}#faq`,
    mainEntity: items.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };
}

function breadcrumb(items: { name: string; path: string }[]): JsonLd {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: [{ name: BRAND, path: '/' }, ...items].map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: url(item.path),
    })),
  };
}

function blogPosting(article: BlogArticleWithMeta): JsonLd {
  return {
    '@type': 'BlogPosting',
    '@id': `${url(`/blog/${article.slug}`)}#artigo`,
    headline: article.title,
    description: article.description,
    abstract: plainText(article.excerpt),
    datePublished: article.publishedAt,
    dateModified: article.updatedAt,
    inLanguage: 'pt-BR',
    articleSection: article.category,
    keywords: article.keywords.join(', '),
    timeRequired: `PT${article.readingMinutes}M`,
    image: `${SITE_URL}/og-image.png`,
    url: url(`/blog/${article.slug}`),
    mainEntityOfPage: url(`/blog/${article.slug}`),
    author: { '@id': `${SITE_URL}/#organizacao` },
    publisher: { '@id': `${SITE_URL}/#organizacao` },
    isPartOf: { '@id': `${SITE_URL}/blog#blog` },
  };
}

function blog(): JsonLd {
  return {
    '@type': 'Blog',
    '@id': `${SITE_URL}/blog#blog`,
    name: `Blog do ${BRAND}`,
    description: BLOG_DESCRIPTION,
    url: url('/blog'),
    inLanguage: 'pt-BR',
    publisher: { '@id': `${SITE_URL}/#organizacao` },
    blogPost: BLOG_ARTICLES.map(a => ({
      '@type': 'BlogPosting',
      headline: a.title,
      url: url(`/blog/${a.slug}`),
      datePublished: a.publishedAt,
    })),
  };
}

/** Perguntas exibidas na página (as mesmas usadas no FAQ visível) */
export function faqForPage(page: Pick<PublicPage, 'segment' | 'article'>) {
  if (page.article) return page.article.faq;
  if (!page.segment) return FAQ_ITEMS;
  const general = [
    'Como funciona o teste grátis de 30 dias?',
    'Vocês cobram taxa ou comissão por pedido?',
    'Existe fidelidade ou multa para cancelar?',
    'Preciso instalar algum programa ou comprar equipamento?',
  ];
  return [...page.segment.faq, ...FAQ_ITEMS.filter(i => general.includes(i.question))];
}

/**
 * @param profiles perfis oficiais (sameAs): os do painel Super Admin, lidos
 *   da API no build; sem eles, os de VITE_SOCIAL_PROFILES
 */
export function buildJsonLd(
  page: PublicPage,
  plans: SubscriptionPlan[],
  profiles: string[] = OFFICIAL_PROFILES,
): JsonLd {
  const graph: JsonLd[] = [
    organization(profiles.length > 0 ? profiles : OFFICIAL_PROFILES),
    website(),
  ];
  if (page.kind === 'article' && page.article) {
    graph.push(blogPosting(page.article));
    if (page.article.faq.length > 0) graph.push(faqPage(page.path, page.article.faq));
    graph.push(
      breadcrumb([
        { name: 'Blog', path: '/blog' },
        { name: page.article.title, path: page.path },
      ]),
    );
  } else if (page.kind === 'legal') {
    graph.push(breadcrumb([{ name: 'Política de Privacidade', path: page.path }]));
  } else if (page.kind === 'blog') {
    graph.push(blog(), breadcrumb([{ name: 'Blog', path: '/blog' }]));
  } else {
    graph.push(softwareApplication(plans, page.segment));
    graph.push(faqPage(page.path, faqForPage(page)));
    if (page.segment) {
      graph.push(breadcrumb([{ name: page.segment.label, path: page.path }]));
    }
  }
  return { '@context': 'https://schema.org', '@graph': graph };
}

// =========================================================================
// Sitemap
// =========================================================================

export function buildSitemap(lastmod: string): string {
  const priority: Record<PublicPage['kind'], string> = {
    home: '1.0',
    segment: '0.8',
    blog: '0.7',
    article: '0.6',
    legal: '0.2',
  };
  const entries = [
    ...PUBLIC_PAGES.map(p => ({
      loc: url(p.path),
      priority: priority[p.kind],
      lastmod: p.article?.updatedAt ?? lastmod,
    })),
    { loc: url('/cadastro'), priority: '0.5', lastmod },
  ];
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
    ...entries.map(
      e =>
        `  <url><loc>${e.loc}</loc><lastmod>${e.lastmod}</lastmod><priority>${e.priority}</priority></url>`,
    ),
    '</urlset>',
    '',
  ].join('\n');
}

// =========================================================================
// llms.txt e llms-full.txt (https://llmstxt.org)
// =========================================================================

function plansMarkdown(plans: SubscriptionPlan[]): string {
  return plans
    .map(plan => {
      const price = plan.isTrial
        ? `Grátis por ${plan.trialDays} dias, sem cartão de crédito`
        : `${money(plan.priceMonthly)}/mês${
            plan.priceYearly ? ` ou ${money(plan.priceYearly)}/ano` : ''
          }`;
      const features = plan.features.map(f => `  - ${f}`).join('\n');
      return `### ${plan.name}: ${price}\n\n${plan.description ?? ''}\n\n${features}`;
    })
    .join('\n\n');
}

export function buildLlmsTxt(plans: SubscriptionPlan[]): string {
  const paid = paidPlans(plans);
  const priceLine =
    paid.length > 0
      ? `Planos de ${money(Math.min(...paid.map(p => p.priceMonthly)))} a ${money(Math.max(...paid.map(p => p.priceMonthly)))} por mês, com teste grátis de 30 dias sem cartão de crédito.`
      : 'Teste grátis de 30 dias sem cartão de crédito.';
  return `# ${BRAND}

> ${COMPANY_SUMMARY} ${priceLine}

Público: pizzarias, hamburguerias, lanchonetes, restaurantes, padarias, confeitarias, barbearias, açaiterias, marmitarias, empórios e adegas no Brasil.

Principais diferenciais:
- Mensalidade fixa e pedidos ilimitados, sem comissão por pedido (marketplaces cobram de 12% a 27%).
- Robô de WhatsApp que atende e fecha o pedido completo pela conversa.
- PIX confirmado direto no banco do estabelecimento e cartão online com pré-autorização.
- Delivery, salão com QR Code na mesa, estoque, financeiro e nota fiscal no mesmo sistema.
- Loja pronta no cadastro, self-service, teste grátis de 30 dias.

## Páginas

- [Página inicial](${url('/')}): visão geral, funcionalidades, calculadora de economia, planos e dúvidas
${SEGMENT_PAGES.map(s => `- [${BRAND} para ${s.audience}](${url(`/para/${s.slug}`)}): ${s.description}`).join('\n')}
- [Criar conta grátis](${url('/cadastro')}): teste de 30 dias sem cartão de crédito

## Blog (guias práticos)

${BLOG_ARTICLES.map(a => `- [${a.title}](${url(`/blog/${a.slug}`)}): ${plainText(a.excerpt)}`).join('\n')}

## Documentação completa

- [llms-full.txt](${SITE_URL}/llms-full.txt): funcionalidades, segmentos, pagamentos, planos, implantação e perguntas frequentes em texto
`;
}

export function buildLlmsFullTxt(plans: SubscriptionPlan[]): string {
  const features = FEATURE_GROUPS.map(
    g =>
      `### ${g.label}: ${g.headline}\n\n${g.description}\n\n${g.items
        .map(i => `- **${i.title}**: ${i.description}`)
        .join('\n')}`,
  ).join('\n\n');

  const pains = PAIN_POINTS.map(
    p => `- **${p.pain}**: ${p.description} Solução: ${p.solution}`,
  ).join('\n');

  const segments = SEGMENT_PAGES.map(
    s => `### ${s.label} (${url(`/para/${s.slug}`)})

${s.heroSubtitle}

Problemas comuns:
${s.pains.map(p => `- **${p.title}**: ${p.description}`).join('\n')}

Como o ${BRAND} resolve:
${s.solutions.map(p => `- **${p.title}**: ${p.description}`).join('\n')}
${s.howItWorks ? `\nComo funciona:\n${s.howItWorks.map((h, i) => `${i + 1}. **${h.title}**: ${h.description}`).join('\n')}\n` : ''}
Perguntas frequentes:
${s.faq.map(f => `- **${f.question}** ${f.answer}`).join('\n')}`,
  ).join('\n\n');

  const comparison = COMPARISON_ROWS.map(r => {
    const v = (x: string | boolean) => (x === true ? 'Sim' : x === false ? 'Não' : x);
    return `| ${r.label} | ${v(r.vendeai)} | ${v(r.marketplace)} | ${v(r.others)} |`;
  }).join('\n');

  const blogGuides = BLOG_ARTICLES.map(
    a => `### ${a.title} (${url(`/blog/${a.slug}`)})

${plainText(a.excerpt)}
${a.faq.length ? `\n${a.faq.map(f => `- **${f.question}** ${plainText(f.answer)}`).join('\n')}\n` : ''}`,
  ).join('\n\n');

  return `# ${BRAND}: informações completas

> ${COMPANY_SUMMARY}

Site: ${url('/')} · Idioma: português (Brasil) · Atendimento: Brasil

## Para quem é

Estabelecimentos que vendem por delivery, retirada, balcão, salão ou agendamento: pizzarias, hamburguerias, lanchonetes, restaurantes, padarias, confeitarias, barbearias, açaiterias, marmitarias, sorveterias, empórios e adegas.

## Problemas que o ${BRAND} resolve

${pains}

## Funcionalidades

${features}

## Segmentos atendidos

${segments}

## ${BRAND} x marketplace x outros sistemas

| Item | ${BRAND} | Marketplace | Outros sistemas |
|---|---|---|---|
${comparison}

## Planos e preços

Todos os planos têm pedidos ilimitados, sem comissão por pedido e sem fidelidade. Os valores são os vigentes na data de geração deste arquivo; a referência é a seção de planos do site (${url('/')}#planos).

${plansMarkdown(plans)}

## Implantação

${ONBOARDING_STEPS.map((s, i) => `${i + 1}. **${s.title}** (${s.time}): ${s.description}`).join('\n')}

## Perguntas frequentes

${FAQ_ITEMS.map(f => `### ${f.question}\n\n${f.answer}`).join('\n\n')}

## Guias do blog

${blogGuides}
`;
}
